import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'dyssc_customer_token';

const api = axios.create({
  baseURL: `${BASE}/api`,
  withCredentials: true, // Google sign-in uses an httpOnly session cookie
});
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Works with either the OTP bearer token or the Google session cookie
      const { data } = await api.get('/customer/me');
      setCustomer(data.customer);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setCustomer(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    // Admin panel never uses the customer session — skip the /customer/me
    // round-trips (and their 401 noise) on /admin routes.
    if (window.location.pathname.startsWith('/admin')) {
      setChecking(false);
      return;
    }
    refresh();
  }, [refresh]);

  const requestOtp = (phone, email, name = '') =>
    api.post('/customer/auth/request-otp', { phone, email, name }).then((r) => r.data);

  const requestLoginOtp = (phone) =>
    api.post('/customer/auth/login-otp', { phone }).then((r) => r.data);

  const verifyOtp = async (phone, email, code) => {
    const { data } = await api.post('/customer/auth/verify-otp', { phone, email, code });
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      setCustomer(data.customer);
    }
    return data;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = (next) => {
    const nextParam = (typeof next === 'string' && next.startsWith('/'))
      ? `&next=${encodeURIComponent(next)}` : '';
    window.location.href =
      `${BASE}/api/customer/auth/google/login?origin=${encodeURIComponent(window.location.origin)}${nextParam}`;
  };

  const logout = () => {
    api.post('/customer/auth/logout').catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setCustomer(null);
  };

  const updateProfile = async (patch) => {
    const { data } = await api.patch('/customer/me', patch);
    setCustomer(data.customer);
    return data.customer;
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer, checking, requestOtp, requestLoginOtp, verifyOtp, logout, refresh, updateProfile,
      loginWithGoogle, api,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);
export const customerApi = api;
